import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { CheckIcon } from 'lucide-react';
import ApplyButton from './ApplyButton';
interface PillarProps {
  number: number;
  title: string;
  description: string;
  getPoints: string[];
  delay: number;
}
const Pillar = ({
  number,
  title,
  description,
  getPoints,
  delay
}: PillarProps) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });
  const variants = {
    hidden: {
      opacity: 0,
      y: 50
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1 + delay,
        duration: 0.6,
        ease: 'easeOut'
      }
    })
  };
  return <div ref={ref} className="relative mb-16 last:mb-0" data-id="element-302">
      {/* Timeline vertical line */}
      <div className="absolute left-[22px] top-0 bottom-0 w-[2px] bg-gray-700" data-id="element-303" />
      {/* Timeline node */}
      <motion.div initial={{
      scale: 0,
      opacity: 0
    }} animate={inView ? {
      scale: 1,
      opacity: 1
    } : {
      scale: 0,
      opacity: 0
    }} transition={{
      delay: delay,
      duration: 0.4
    }} className="absolute left-0 top-0 w-[45px] h-[45px] rounded-full bg-[#ffdf07] flex items-center justify-center text-black font-bold z-10" data-id="element-304">
        {number}
      </motion.div>
      {/* Content container */}
      <div className="ml-[70px] pt-2" data-id="element-305">
        <motion.h3 custom={0} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={variants} className="text-2xl md:text-3xl text-white mb-4 typekit-heading" data-id="element-306">
          Pillar {number}: {title}
        </motion.h3>
        <motion.p custom={1} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={variants} className="text-gray-300 mb-6 libre-caslon-text-regular leading-relaxed" data-id="element-307">
          {description}
        </motion.p>
        <motion.p custom={2} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={variants} className="text-white font-semibold mb-4 libre-caslon-text-regular" data-id="element-308">
          You'll get:
        </motion.p>
        <div className="space-y-3 mb-6" data-id="element-309">
          {getPoints.map((point, index) => <motion.div key={index} custom={index + 3} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={variants} className="flex items-start" data-id="element-310">
              <div className="text-[#ffdf07] mr-3 mt-1 flex-shrink-0" data-id="element-311">
                <CheckIcon size={18} data-id="element-312" />
              </div>
              <p className="text-gray-300 libre-caslon-text-regular leading-relaxed" data-id="element-313">
                {point}
              </p>
            </motion.div>)}
        </div>
      </div>
    </div>;
};
interface TimelineSectionProps {
  scrollToCalendar?: () => void;
}
const TimelineSection = ({
  scrollToCalendar
}: TimelineSectionProps) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [battlePlanRef, battlePlanInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <section className="w-full bg-black py-20 px-4" data-id="element-314">
      <div className="max-w-4xl mx-auto" data-id="element-315">
        <motion.div ref={ref} initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.7
      }} className="text-center mb-8" data-id="element-316">
          <h2 className="text-white text-3xl md:text-4xl mb-6 typekit-heading" data-id="element-317">
            Our Process: The 14‑Day 8‑Figure Clinic MRI™
          </h2>
          <p className="text-gray-300 text-lg libre-caslon-text-regular max-w-3xl mx-auto" data-id="element-318">
            We run your $3M–$25M clinic group through the four lenses serious
            capital actually uses – then turn that into a 30‑Day, 90‑Day, and
            12–24 Month plan to grow faster and move toward a life‑changing
            check.
          </p>
        </motion.div>
        <div className="relative mt-16" data-id="element-319">
          <Pillar number={1} title="Operations" description="We pressure‑test your operations the way a buyer would and show you where things break when you try to grow." getPoints={['A clear view of how much you can really scale before chaos hits', 'A map of where systems, staffing, and decision‑making need to be tightened', 'Identification of the "owner‑bottleneck" points that scare serious buyers', 'Specific operational upgrades needed to support more locations, more volume, more revenue']} delay={0.2} data-id="element-320" />
          <Pillar number={2} title="Marketing & Growth" description="We diagnose whether you have a controllable growth engine or you're just riding referrals, relationships, and luck." getPoints={['A breakdown of where your patient volume really comes from (and how reliable it is)', 'A split between controllable vs accidental growth', 'An outside view of how buyers will judge your reputation and market presence', 'Simple, practical moves to increase volume without killing margins or burning your team']} delay={0.4} data-id="element-321" />
          <Pillar number={3} title="Data & Payers" description="We go under the hood on the claims, payers, and data that quietly decide how profitable and safe you look to real money." getPoints={['A map of where your cash is getting stuck in the reimbursement cycle', 'Identification of denials and errors that are silently draining profit', 'A ranking of which providers and payers drive your strongest – and weakest – earnings', 'A red/amber/green read on your data quality and what would blow up in diligence']} delay={0.6} data-id="element-322" />
          <Pillar number={4} title="Financials" description="We rebuild your numbers the way Big 4 and serious buyers do – not your tax CPA – so you finally see the real profit they're willing to underwrite." getPoints={['A buyer‑style view of your true earnings (not just your tax return)', 'Clarity on which parts of your revenue are worth the most – and which get valued at zero', 'A list of where money is being left on the table or mis‑measured', 'A point‑by‑point breakdown of what a buyer will attack, question, or discount immediately']} delay={0.8} data-id="element-323" />
        </div>
        {/* Battle Plan Section */}
        <motion.div ref={battlePlanRef} initial={{
        opacity: 0,
        y: 20
      }} animate={battlePlanInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.7
      }} className="mt-20 pt-16 border-t border-gray-800" data-id="element-324">
          <p className="text-gray-300 text-lg libre-caslon-text-regular mb-8 text-center" data-id="element-325">
            When all four pillars are complete, you don't get a "report."
          </p>
          <p className="text-[#ffdf07] text-xl libre-caslon-text-regular mb-12 text-center font-semibold" data-id="element-326">
            You get a battle plan built for an 8‑figure future:
          </p>
          <div className="space-y-8" data-id="element-327">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={battlePlanInView ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="bg-gradient-to-b from-[#111111] to-[#0a0a0a] border border-gray-800 rounded-lg p-6" data-id="element-328">
              <h4 className="text-white text-xl mb-3 typekit-heading" data-id="element-329">
                30‑Day Wins
              </h4>
              <p className="text-gray-300 libre-caslon-text-regular mb-3" data-id="element-330">
                Quick hits you can execute immediately to:
              </p>
              <ul className="space-y-2" data-id="element-331">
                <li className="flex items-start" data-id="element-332">
                  <CheckIcon className="text-[#ffdf07] mr-3 mt-1 flex-shrink-0" size={18} data-id="element-333" />
                  <span className="text-gray-300 libre-caslon-text-regular" data-id="element-334">
                    Put cash back in your pocket
                  </span>
                </li>
                <li className="flex items-start" data-id="element-335">
                  <CheckIcon className="text-[#ffdf07] mr-3 mt-1 flex-shrink-0" size={18} data-id="element-336" />
                  <span className="text-gray-300 libre-caslon-text-regular" data-id="element-337">
                    Kill obvious risks buyers would use against you
                  </span>
                </li>
                <li className="flex items-start" data-id="element-338">
                  <CheckIcon className="text-[#ffdf07] mr-3 mt-1 flex-shrink-0" size={18} data-id="element-339" />
                  <span className="text-gray-300 libre-caslon-text-regular" data-id="element-340">
                    Stop stupid leaks in ops, payers, and process
                  </span>
                </li>
              </ul>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={battlePlanInView ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="bg-gradient-to-b from-[#111111] to-[#0a0a0a] border border-gray-800 rounded-lg p-6" data-id="element-341">
              <h4 className="text-white text-xl mb-3 typekit-heading" data-id="element-342">
                90‑Day Upgrade Plan
              </h4>
              <p className="text-gray-300 libre-caslon-text-regular mb-3" data-id="element-343">
                The high‑leverage moves that:
              </p>
              <ul className="space-y-2" data-id="element-344">
                <li className="flex items-start" data-id="element-345">
                  <CheckIcon className="text-[#ffdf07] mr-3 mt-1 flex-shrink-0" size={18} data-id="element-346" />
                  <span className="text-gray-300 libre-caslon-text-regular" data-id="element-347">
                    Make your group look investable, not just busy
                  </span>
                </li>
                <li className="flex items-start" data-id="element-348">
                  <CheckIcon className="text-[#ffdf07] mr-3 mt-1 flex-shrink-0" size={18} data-id="element-349" />
                  <span className="text-gray-300 libre-caslon-text-regular" data-id="element-350">
                    Tighten the systems buyers care about most
                  </span>
                </li>
                <li className="flex items-start" data-id="element-351">
                  <CheckIcon className="text-[#ffdf07] mr-3 mt-1 flex-shrink-0" size={18} data-id="element-352" />
                  <span className="text-gray-300 libre-caslon-text-regular" data-id="element-353">
                    Start shifting you from "nice business" to "serious
                    acquisition candidate"
                  </span>
                </li>
              </ul>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={battlePlanInView ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} transition={{
            duration: 0.6,
            delay: 0.6
          }} className="bg-gradient-to-b from-[#111111] to-[#0a0a0a] border border-gray-800 rounded-lg p-6" data-id="element-354">
              <h4 className="text-white text-xl mb-3 typekit-heading" data-id="element-355">
                12–24 Month Scale Map
              </h4>
              <p className="text-gray-300 libre-caslon-text-regular mb-3" data-id="element-356">
                A clear path to:
              </p>
              <ul className="space-y-2" data-id="element-357">
                <li className="flex items-start" data-id="element-358">
                  <CheckIcon className="text-[#ffdf07] mr-3 mt-1 flex-shrink-0" size={18} data-id="element-359" />
                  <span className="text-gray-300 libre-caslon-text-regular" data-id="element-360">
                    Build a clinic group that can credibly justify a 7–8 figure
                    check
                  </span>
                </li>
                <li className="flex items-start" data-id="element-361">
                  <CheckIcon className="text-[#ffdf07] mr-3 mt-1 flex-shrink-0" size={18} data-id="element-362" />
                  <span className="text-gray-300 libre-caslon-text-regular" data-id="element-363">
                    Grow cleaner and faster now
                  </span>
                </li>
                <li className="flex items-start" data-id="element-364">
                  <CheckIcon className="text-[#ffdf07] mr-3 mt-1 flex-shrink-0" size={18} data-id="element-365" />
                  <span className="text-gray-300 libre-caslon-text-regular" data-id="element-366">
                    Avoid waking up at the first buyer meeting realizing you
                    spent 20 years building the wrong thing
                  </span>
                </li>
              </ul>
            </motion.div>
          </div>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={battlePlanInView ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} transition={{
          duration: 0.6,
          delay: 0.8
        }} className="text-gray-300 text-lg libre-caslon-text-regular mt-12 text-center font-semibold" data-id="element-367">
            Summary: Same clinics. Same owner. Different structure. Different
            perception. Very different outcome.
          </motion.p>
        </motion.div>
        <div className="mt-12" data-id="element-368">
          {scrollToCalendar ? <div className="flex justify-center" data-id="element-369">
              <div className="relative" data-id="element-370">
                <div className="button-border-light" data-id="element-371"></div>
                <motion.button onClick={scrollToCalendar} whileHover={{
              scale: 1.05,
              boxShadow: '0px 8px 20px rgba(255, 223, 7, 0.3)'
            }} className="relative z-10 bg-gradient-to-r from-[#ffdf07] via-[#fae651] to-[#ffdf07] text-black font-semibold py-3 px-8 sm:px-10 rounded-md text-base shadow-[0_4px_14px_rgba(255,223,7,0.25)] border border-[#ffd70077] transition-all duration-300" data-id="element-372">
                  Book My Strategy Call
                </motion.button>
              </div>
            </div> : <ApplyButton data-id="element-373" />}
        </div>
      </div>
    </section>;
};
export default TimelineSection;